import { Fragment } from 'react';
import {
   Box,
   Button,
   Link,
   Table,
   Tbody,
   Td,
   Th,
   Thead,
   Tr,
   Text,
   useToast,
} from '@chakra-ui/react';
import { _js } from '@ifixit/localize';
import { type CartLineItem } from '@ifixit/shopify-cart-sdk';
import { useAddToCart } from '@ifixit/shopify-cart-sdk/hooks';
import API_2_0 from 'Shared/api_2_0';
import { OrderType } from './orders_types';
import { useShopifyStorefrontClient } from '@ifixit/shopify-storefront-client';

export function OrdersTable({
   orders,
   fromShopify,
}: {
   orders: OrderType[];
   fromShopify: boolean;
}) {
   return (
      <Box w={{ base: '95vw', lg: '100%' }} overflow={{ base: 'scroll', lg: 'unset' }}>
         <Table variant="striped" colorScheme="gray" mt="50px">
            <TableHeaders fromShopify={fromShopify} />
            <TableBody orders={orders} fromShopify={fromShopify} />
         </Table>
      </Box>
   );
}

const TableHeaders = ({ fromShopify }: { fromShopify: boolean }) => (
   <Thead>
      <Tr>
         <Th>{_js('Order #')}</Th>
         <Th>{_js('Status')}</Th>
         <Th>{_js('Date')}</Th>
         <Th isNumeric>{_js('Total')}</Th>
         <Th>{_js('Carrier')}</Th>
         <Th>{_js('Track')}</Th>
         {!fromShopify && <Th></Th>}
      </Tr>
   </Thead>
);

const TableBody = ({ orders, fromShopify }: { orders: OrderType[]; fromShopify: boolean }) => {
   const { storeCode } = useShopifyStorefrontClient();
   const toast = useToast();
   const { addToCart, enabled: addToCartEnabled } = useAddToCart();

   return (
      <Tbody>
         {orders.map((order: OrderType) => {
            const buyItAgain = async () => {
               const res = await new API_2_0(
                  `/User/Orders/orderItemsForCart/${storeCode}/${order.orderid}`
               ).send();
               const lines = res.lines as CartLineItem[];
               if (lines.length > 0) {
                  addToCart.mutate(
                     { analytics: { descriptor: 'Buy it Again' }, lines },
                     { onSuccess: () => (window.location.href = '/cart') }
                  );
               } else {
                  toast({
                     title: _js('Items unavailable'),
                     description: _js('No items from order %1 are available.', order.order_name),
                     status: 'error',
                     duration: 9000,
                     isClosable: true,
                  });
               }
            };
            return (
               <Tr key={order.orderid}>
                  <Td py="5px" color="brand.500">
                     <Link color="brand.500" href={order.invoice_link}>
                        {order.order_name}
                     </Link>
                  </Td>
                  <Td py="5px" className="orderStatus">
                     {order.status}
                     {order.editable && order.cancel_order_link && (
                        <Fragment>
                           <Link
                              ml="4px"
                              color="brand.500"
                              href={order.cancel_order_link}
                              className="cancelLink"
                           >
                              {_js('[Cancel Order]')}
                           </Link>
                        </Fragment>
                     )}
                  </Td>
                  <Td py="5px">{new Date(order.init_date * 1000).toLocaleDateString()}</Td>
                  <Td py="5px">{getFormattedTotal(order.total, order.currency_type)}</Td>
                  <Td py="5px" whiteSpace="nowrap">
                     {order.shipment_tracking &&
                        order.shipment_tracking.length > 0 &&
                        order.shipment_tracking.map(tracking => (
                           <Text key={`carrier-${tracking.number}`} whiteSpace="nowrap">
                              {tracking.carrier}
                           </Text>
                        ))}
                  </Td>
                  <Td py="5px">
                     {/* @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'. */}
                     {order.showTracking && order.shipment_tracking?.length > 0
                        ? order.shipment_tracking?.map(tracking => {
                             if (!tracking.link) {
                                return (
                                   <Text key={`nolink-${tracking.number}`}>{tracking.number}</Text>
                                );
                             }
                             return (
                                <Link key={`link-${tracking.number}`} href={tracking.link}>
                                   <Text>{tracking.number}</Text>
                                </Link>
                             );
                          })
                        : _js('No tracking')}
                  </Td>
                  {!fromShopify && (
                     <Td py="5px">
                        <Button
                           onClick={buyItAgain}
                           isLoading={addToCart.isPending}
                           isDisabled={!addToCartEnabled}
                        >
                           {_js('Buy it again')}
                        </Button>
                     </Td>
                  )}
               </Tr>
            );
         })}
      </Tbody>
   );
};

function getFormattedTotal(total: number, currency_type: string) {
   const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency: currency_type,
   };

   const formatter = new Intl.NumberFormat(undefined, formatOptions);
   return formatter.format(total);
}
